// PRD-0191 brand visuals: a blog post's hero/OG slots (select, regenerate) and the brand visual kit.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { isNotFoundError } from "../lib/load-state";
import { SIGNED_URL_QUERY_OPTIONS, signedUrlRefetchMs } from "../lib/signed-url-refresh";
import type {
  BrandVisualKit,
  BriefVisuals,
  VisualKitFontUploadInput,
  VisualKitPreview,
  VisualKitImportInput,
  VisualKitPutInput,
  VisualSlot,
  VisualsGenerateAccepted,
  VisualsGenerateInput,
} from "../../../shared/visuals";

function visualsPath(brandId: string, briefId: string) {
  return `/api/v1/brands/${encodeURIComponent(brandId)}/briefs/${encodeURIComponent(briefId)}/visuals`;
}
export function kitPath(brandId: string) {
  return `/api/v1/brands/${encodeURIComponent(brandId)}/visual-kit`;
}

function visualsExpiries(visuals: BriefVisuals | undefined): Array<string | null | undefined> {
  if (!visuals) return [];
  return Object.values(visuals.slots).flatMap((group) => [group.selected, ...group.proposals])
    .map((entry) => entry?.asset?.url_expires_at);
}

export function briefVisualsQueryKey(brandId: string | undefined, briefId: string | undefined) {
  return ["brief-visuals", brandId, briefId] as const;
}
export function visualKitQueryKey(brandId: string | undefined) {
  return ["visual-kit", brandId] as const;
}

/**
 * Slots for one blog post. While `pollUntil` is in the future (a generation was just started) it
 * refetches every 8s so new proposals appear without a reload.
 */
export function useBriefVisuals(brandId: string | undefined, briefId: string | undefined, pollUntil = 0) {
  return useQuery({
    queryKey: briefVisualsQueryKey(brandId, briefId),
    queryFn: async () => (await api.get<{ data: BriefVisuals }>(visualsPath(brandId!, briefId!))).data,
    enabled: !!brandId && !!briefId,
    retry: false,
    ...SIGNED_URL_QUERY_OPTIONS,
    // Poll while a generation runs; otherwise refresh before the assets' signed URLs expire.
    refetchInterval: (query) => (Date.now() < pollUntil ? 8_000 : signedUrlRefetchMs(visualsExpiries(query.state.data))),
  });
}

export function useGenerateVisuals(brandId: string | undefined, briefId: string | undefined) {
  return useMutation({
    mutationFn: async (input: VisualsGenerateInput) => {
      if (!brandId || !briefId) throw new Error("Missing brand or brief context");
      return (await api.post<{ data: VisualsGenerateAccepted }>(`${visualsPath(brandId, briefId)}/generate`, input)).data;
    },
  });
}

export function useSelectVisual(brandId: string | undefined, briefId: string | undefined, campaignId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ slot, assetId }: { slot: VisualSlot; assetId: string }) => {
      if (!brandId || !briefId) throw new Error("Missing brand or brief context");
      return (await api.put<{ data: BriefVisuals }>(`${visualsPath(brandId, briefId)}/${slot}`, { asset_id: assetId })).data;
    },
    onSuccess: (visuals) => {
      qc.setQueryData(briefVisualsQueryKey(brandId, briefId), visuals);
    },
    onSettled: () => {
      // Changing images on an approved post revokes approval and cancels Ready server-side.
      qc.invalidateQueries({ queryKey: ["publish-handoff", brandId, briefId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review-approved", brandId] });
      if (campaignId) qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
    },
  });
}

/**
 * Live card preview for the saved kit: the API stores the render in Mech Storage and returns a signed
 * URL (the browser cannot load image bytes from the API origin). Keyed by kit version + title.
 */
export function useKitPreview(brandId: string | undefined, version: number | undefined, title: string, enabled: boolean) {
  return useQuery({
    queryKey: ["visual-kit-preview", brandId, version, title] as const,
    queryFn: async () => (await api.get<{ data: VisualKitPreview }>(`${kitPath(brandId!)}/preview?${new URLSearchParams({ title })}`)).data,
    enabled: !!brandId && enabled && !!title,
    retry: false,
    // Signed URLs last about an hour; refresh well before they expire.
    ...SIGNED_URL_QUERY_OPTIONS,
    refetchInterval: (query) => signedUrlRefetchMs([query.state.data?.expires_at]),
  });
}

/** null = the brand has no kit yet. */
export function useVisualKit(brandId: string | undefined) {
  return useQuery({
    queryKey: visualKitQueryKey(brandId),
    queryFn: async (): Promise<BrandVisualKit | null> => {
      try {
        return (await api.get<{ data: BrandVisualKit }>(kitPath(brandId!))).data;
      } catch (error) {
        if (isNotFoundError(error)) return null;
        throw error;
      }
    },
    enabled: !!brandId,
    retry: false,
  });
}

function useKitMutation<I>(brandId: string | undefined, run: (brandId: string, input: I) => Promise<BrandVisualKit>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: I) => {
      if (!brandId) throw new Error("Missing brand context");
      return run(brandId, input);
    },
    onSuccess: (kit) => qc.setQueryData(visualKitQueryKey(brandId), kit),
  });
}

export function useSaveVisualKit(brandId: string | undefined) {
  return useKitMutation<VisualKitPutInput>(brandId, async (id, input) => (await api.put<{ data: BrandVisualKit }>(kitPath(id), input)).data);
}

export function useImportVisualKit(brandId: string | undefined) {
  return useKitMutation<VisualKitImportInput>(brandId, async (id, input) =>
    (await api.post<{ data: { kit: BrandVisualKit; unmapped: string[] } }>(`${kitPath(id)}/import`, input)).data.kit);
}

export function useUploadKitFont(brandId: string | undefined) {
  return useKitMutation<VisualKitFontUploadInput>(brandId, async (id, input) =>
    (await api.post<{ data: BrandVisualKit }>(`${kitPath(id)}/fonts`, input)).data);
}
